import React from 'react';
import { ExportExcel, Print, TimerClock } from '@elliemae/ds-icons';
import { DSPageHeader } from '@elliemae/ds-page-header';
import { DSToolbar, ToolbarItem } from '@elliemae/ds-toolbar';
import { DSTooltipV2, tooltipPositions } from '@elliemae/ds-tooltip';
import { useLoanDataFieldSelector } from '@frontend/pricing-search';
import { any, bool, func, node, shape } from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RequestLockStatus } from '../../../../common/constants/enumTypes';
import { errorMessages } from '../../../../common/constants/Messages';
import {
  getRateSheetDocument,
  validateRateDocInstance,
} from '../../../../common/services/lockSummaryServices';
import api from '../../../../common/utils/api';
import { openGlobalErrorModalAction } from '../../../../data/globalErrorModal/actionCreators';
import {
  useRateSelector,
  useRateSelectorLien,
  useSelectedProductSelector,
} from '../../../../data/useSelectors';
import { AppDispatch } from '../../../../store';
import { RateAlert } from '../RateAlertModal/RateAlertContainer';
import { buildLockSummaryDocument } from './helpers';

export const HeaderDetailsToolbar = ({
  onPrintClick,
  onRateAlertClick,
  printOnly = false,
}) => {
  const { rateDocInstanceID } = useSelectedProductSelector();
  const [isExportExcel, setIsExportExcel] = useState(false);

  useEffect(() => {
    if (printOnly) {
      return;
    }
    // TODO we should check this. The return of validateRateDocInstance is not a Promise
    validateRateDocInstance(rateDocInstanceID).then((data) => {
      setIsExportExcel(data);
    });
  }, []);

  return (
    <DSToolbar
      withDepth={false}
      containerProps={{ style: { padding: 0 } }}
      autoFocusOnMount={false}
    >
      {!printOnly && (
        <DSTooltipV2
          placement={tooltipPositions.BOTTOM}
          title="Rate Alert"
          triggerComponent={
            <ToolbarItem icon={<TimerClock />} onClick={onRateAlertClick} />
          }
        />
      )}
      <DSTooltipV2
        placement={tooltipPositions.BOTTOM}
        title="Print"
        triggerComponent={
          <ToolbarItem icon={<Print />} onClick={onPrintClick} />
        }
      />
      {!printOnly && (
        <DSTooltipV2
          placement={tooltipPositions.BOTTOM}
          title="Download Rate Sheet"
          triggerComponent={
            <ToolbarItem
              style={{ display: isExportExcel ? 'block' : 'none' }}
              icon={<ExportExcel />}
              onClick={async () => {
                getRateSheetDocument(rateDocInstanceID);
              }}
            />
          }
        />
      )}
    </DSToolbar>
  );
};

HeaderDetailsToolbar.propTypes = {
  onPrintClick: func.isRequired,
  onRateAlertClick: func.isRequired,
  printOnly: bool,
};

const Iframe = ({ content, frameRef }) => {
  const writeHTML = (frame) => {
    frameRef.current = frame;
    if (!frame) {
      return;
    }
    const doc = frame.contentDocument;
    doc.open();
    doc.write(content);
    doc.close();
  };
  return (
    <div style={{ display: 'none' }}>
      <iframe
        title="Print"
        src="about:blank"
        // allow-modals lets the document open modals.
        sandbox="allow-same-origin allow-modals"
        scrolling="no"
        frameBorder="0"
        ref={writeHTML}
      />
    </div>
  );
};

Iframe.propTypes = {
  content: node.isRequired,
  frameRef: shape({ current: any }).isRequired,
};

const decodeHtml = (value: string) => {
  const node = document.createElement('textarea');
  node.innerHTML = value;
  return node.value;
};

// the document comes from rateSelector bcz /documents/locksummary needs an origin.
export type SummaryHeaderActions = 'all' | 'print' | 'none';

export const SummaryHeader = ({
  actions = 'all',
}: {
  actions?: SummaryHeaderActions;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const rateSelector = useRateSelector() as any;
  const requestLockStatus = useLoanDataFieldSelector('requestLockStatus', -1);
  const { userProgramName, userLender } = useRateSelectorLien(
    ['userProgramName', 'userLender'],
    '',
  ) as any;
  const programNameValue = userProgramName ?? '';
  const userLenderValue = userLender ?? '';
  const [documentHTML, setDocumentHTML] = useState('');
  const [isRequestedModalOpen, setisRequestedModalOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const decodedDocumentHTML = useMemo(
    () => decodeHtml(documentHTML),
    [documentHTML],
  );

  // Ensure requestLockStatus is of type RequestLockStatus
  // and use a config object for mapping status to string

  // If requestLockStatus is not already typed, cast or coerce it
  const status: RequestLockStatus =
    typeof requestLockStatus === 'number'
      ? (requestLockStatus as RequestLockStatus)
      : RequestLockStatus.NotLocked;

  const lockStatusConfig: Record<RequestLockStatus, string> = {
    [RequestLockStatus.NotLocked]: 'Lock Request:',
    [RequestLockStatus.NoStatus]: 'Lock Request:',
    [RequestLockStatus.ActiveLock]: 'Lock Update:',
    [RequestLockStatus.Cancelled]: 'Re-Lock:',
    [RequestLockStatus.Expired]: '',
  };

  const lockStatusStr = lockStatusConfig[status] ?? 'Lock Request:';

  const printOnly = actions === 'print';
  const showToolbar = actions !== 'none';

  const printFrame = (html?: string) => {
    const iframe = iframeRef.current;
    if (html) {
      const doc = iframe?.contentDocument;
      doc?.open();
      doc?.write(html);
      doc?.close();
    }
    iframe?.focus();
    iframe?.contentWindow?.print?.();
  };

  const notifyPrintFailed = () =>
    dispatch(
      openGlobalErrorModalAction({
        title: 'Print Unavailable',
        errorMessage: errorMessages.LOCK_SUMMARY_DOCUMENT_ERROR,
      }),
    );

  const handlePrintClick = async () => {
    const comments = rateSelector?.loan?.comments || '';

    if (printOnly) {
      const summaryDocument = buildLockSummaryDocument(
        rateSelector?.loan?.optimalHistory,
        comments,
      );
      if (!summaryDocument) {
        notifyPrintFailed();
        return;
      }
      printFrame(summaryDocument);
      return;
    }

    try {
      const response = await api.getLockSummaryDocument({
        rateSelectorResponse: rateSelector,
        comments,
      });
      setDocumentHTML(response.body);
      printFrame(decodeHtml(response.body));
    } catch {
      notifyPrintFailed();
    }
  };

  const handleRateAlertClick = () => {
    setisRequestedModalOpen(true);
  };

  const handleRateAlertCancel = () => {
    setisRequestedModalOpen(false);
  };

  const title =
    `${lockStatusStr} ${userLenderValue} ${programNameValue}`.trim();

  return (
    <>
      {actions === 'all' && (
        <RateAlert
          isOpen={isRequestedModalOpen}
          handleCancel={handleRateAlertCancel}
        />
      )}
      <DSPageHeader
        renderToolbar={
          showToolbar
            ? () => (
                <HeaderDetailsToolbar
                  printOnly={printOnly}
                  onPrintClick={handlePrintClick}
                  onRateAlertClick={handleRateAlertClick}
                />
              )
            : undefined
        }
        title={title}
      />
      {showToolbar && (
        <Iframe content={decodedDocumentHTML} frameRef={iframeRef} />
      )}
    </>
  );
};
